import React, { FormEvent, useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, FileDown, X } from "lucide-react";
import { Sensor } from "../../../types";
import {
  getSensorSchemaFromCategoryAndType,
  getSchemaTypeFromCategoryAndType,
} from "../sensors/sensorSchemas";
import DynamicCalibrationDisplay from "../sensors/DynamicCalibrationDisplay";
import toast from "react-hot-toast";
import { formatSensorType } from "../../../shared/utils/formatters";
import { formatMeasurementRangeLabel } from "../utils/rangeUnit";
import { CertificateFormData } from "../types/certificate";
import { CertificateFieldConfig, SectionConfig, TableConfig } from "../sensors/types";
import { certificateService, type CertificateResponse } from "../services/certificate.service";
import {
  buildCertificateFormData,
  canShowCertificateAction,
} from "../utils/certificateFormData";
import {
  buildCertificateDisplayContext,
  resolveCertificateFieldUnit,
} from "../utils/certificateFormContext";
import { getStatusBadge } from "../components/Badge";
import { StatusCard } from "../components/StatusCard";
import { InfoGrid, InfoItemData } from "../components/InfoGrid";
import { DetailSection } from "../components/DetailSection";
import { TabNavigation, TabItem } from "../components/TabNavigation";
import { MetricCard } from "../components/MetricCard";
import { DisplayLabelContext, FieldConfig } from "../sensors/types";

/** Same dot-path resolution DynamicCalibrationDisplay uses for FieldConfig.key. */
const getNestedValue = (obj: any, path: string): any =>
  path.split(".").reduce((acc, part) => (acc ? acc[part] : undefined), obj);

/** Resolve a schema field into MetricCard props (label/value/unit), reusing its own format/unit fns. */
const resolveMetricField = (
  field: FieldConfig,
  data: any,
  labelContext: DisplayLabelContext
): { label: string; value: string; unit?: string } | null => {
  const raw = getNestedValue(data, field.key);
  if (raw === undefined || raw === null) return null;
  if (field.condition && !field.condition(data)) return null;
  const label = typeof field.label === "function" ? field.label(labelContext) : field.label;
  const unit = typeof field.unit === "function" ? field.unit(labelContext) : field.unit;
  return { label, value: field.format ? field.format(raw) : String(raw), unit };
};

/** Normalize to YYYY-MM-DD for form state and HTML date input. */
const toYYYYMMDD = (dateStr: string | undefined): string => {
  if (!dateStr || !dateStr.trim()) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split(".");
    return `${year}-${month}-${day}`;
  }
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** Always return YYYY-MM-DD for <input type="date"> so the value is never empty/invalid. */
const dateInputValue = (stored: string | undefined): string => {
  const yyyy = toYYYYMMDD(stored);
  if (yyyy) return yyyy;
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
};

/** Convert YYYY-MM-DD to DD.MM.YYYY for API/certificate display. */
const toDDMMYYYY = (dateStr: string): string => {
  if (!dateStr || !dateStr.trim()) return "";
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split("-");
  if (parts.length === 3 && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = parts;
    return `${day}.${month}.${year}`;
  }
  return dateStr;
};

/** Known environmental-condition keys used across sensor schemas (not invented — see displacement/piezometer/accelerometer/tiltmeter/strain_gauge schemas). */
const ENVIRONMENT_FIELDS: { key: string; label: string; unit?: string }[] = [
  { key: "ambient_temperature", label: "Ambient Temperature", unit: "°C" },
  { key: "temperature", label: "Temperature", unit: "°C" },
  { key: "humidity", label: "Humidity", unit: "%" },
  { key: "weather_condition", label: "Weather Condition" },
  { key: "atmospheric_pressure", label: "Atmospheric Pressure" },
];

const getEnvironmentItems = (data: any): InfoItemData[] => {
  if (!data || typeof data !== "object") return [];
  const seen = new Set<string>();
  const items: InfoItemData[] = [];
  ENVIRONMENT_FIELDS.forEach(({ key, label, unit }) => {
    if (seen.has(label)) return;
    const value = data[key];
    if (value === undefined || value === null || value === "") return;
    seen.add(label);
    items.push({ label, value: unit ? `${value} ${unit}` : String(value) });
  });
  return items;
};

/** True if any section/table in a schema half carries the given display group (ignoring runtime conditions). */
const schemaHasGroup = (
  half: { sections?: SectionConfig[]; tables?: TableConfig[] } | undefined,
  group: "setup" | "result" | "measurement" | "analysis"
): boolean =>
  !!half &&
  ((half.sections || []).some((s) => (s.group || "setup") === group) ||
    (half.tables || []).some((t) => (t.group || "setup") === group));

type TabId = "overview" | "sensor" | "calibration" | "results" | "measurements" | "analysis";

const CalibrationDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get data from route state
  const state = location.state as {
    calibration: any;
    sensor: Sensor;
    sensorType?: string;
    sensorCategory?: string;
    sensorModel?: string;
    result?: any; // Result data when navigating from search
    showResult?: boolean; // Flag to show result directly
  } | null;

  const [calibration] = useState<any>(state?.calibration || null);
  const [sensor] = useState<Sensor | null>(state?.sensor || null);
  const [activeTab, setActiveTab] = useState<TabId>("overview");

  const sensorType = state?.sensorType || (sensor as any)?.type || sensor?.sensor_type?.type || null;
  const sensorCategory = state?.sensorCategory || (sensor as any)?.category || sensor?.sensor_type?.category || null;
  const sensorModel = state?.sensorModel || (sensor as any)?.model || sensor?.sensor_type?.model || null;
  const sensorRange = (sensor as any)?.measurement_range ?? sensor?.sensor_type?.measurement_range ?? null;
  const sensorUnit = (sensor as any)?.unit || sensor?.sensor_type?.unit || null;

  // Third arg is a last-resort fallback: if sensor category/type metadata is
  // missing on this record, sniff the actual input_data shape instead of
  // silently defaulting to the displacement schema (see sensorSchemas.ts).
  const schema = getSensorSchemaFromCategoryAndType(sensorCategory, sensorType, calibration?.input_data);

  // For displacement and piezometer sensors, the Measurements tab is folded into
  // Results (no separate tab) per product request — other sensor types keep it.
  const resolvedSchemaType = getSchemaTypeFromCategoryAndType(
    sensorCategory,
    sensorType,
    calibration?.input_data
  );
  const mergeMeasurementsIntoResults =
    resolvedSchemaType === "displacement" || resolvedSchemaType === "piezometer";

  // Result data always comes straight from the calibration record (or route state, when navigating from search)
  const selectedCalibrationResult = state?.result || calibration?.result || null;
  const hasResult = selectedCalibrationResult !== null && selectedCalibrationResult !== undefined;

  const [showCertificateForm, setShowCertificateForm] = useState(false);
  const [certificateFormData, setCertificateFormData] = useState<CertificateFormData | null>(null);
  const [isGeneratingCertificate, setIsGeneratingCertificate] = useState(false);
  const [certificateForResult, setCertificateForResult] = useState<CertificateResponse | null>(null);

  const resultId = selectedCalibrationResult?.id;

  useEffect(() => {
    if (!resultId) {
      setCertificateForResult(null);
      return;
    }
    certificateService.getByResultId(resultId).then((list) => {
      setCertificateForResult(list && list.length > 0 ? list[0] : null);
    }).catch(() => setCertificateForResult(null));
  }, [resultId]);

  // Get certificate form configuration from schema - schema is the source of truth
  const certificateFormFields = schema.certificateFormConfig?.fields || [];
  const certificateFormTitle =
    schema.certificateFormConfig?.title || "Certificate Details";
  const certificateFormDescription = schema.certificateFormConfig?.description;

  const certificateDisplayContext = useMemo(
    () =>
      buildCertificateDisplayContext({
        sensorUnit,
        sensor,
        calibration,
        schemaDefaultUnit: schema.unit,
      }),
    [sensorUnit, sensor, calibration, schema.unit]
  );

  const openCertificateForm = () => {
    try {
      const certificateData = buildCertificateFormData(
        sensorCategory,
        sensorType,
        calibration || { input_data: selectedCalibrationResult?.input_data },
        selectedCalibrationResult,
        { sensorUnit, sensor, calibration, schemaDefaultUnit: schema.unit }
      );
      if (!certificateData) {
        toast.error(
          "Unable to prepare certificate data. Please verify calibration details."
        );
        return;
      }
      setCertificateFormData(certificateData);
      setShowCertificateForm(true);
    } catch (error) {
      toast.error("Something went wrong while preparing the certificate.");
    }
  };

  const closeCertificateForm = () => {
    setShowCertificateForm(false);
    setIsGeneratingCertificate(false);
  };

  const handleCertificateFieldChange = (
    field: keyof CertificateFormData,
    value: string
  ) => {
    setCertificateFormData((prev) => {
      if (!prev) return prev;
      if (field === "date") {
        const next = value.trim();
        return { ...prev, date: next || dateInputValue(prev.date) || prev.date };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleDownloadCertificate = async () => {
    if (!certificateForResult) return;
    try {
      const blob = await certificateService.getDownloadBlob(certificateForResult.id);
      const filename = certificateForResult.filename ?? certificateForResult.certificate_number ?? "certificate";
      certificateService.triggerCertificateDownload(blob, filename);
      toast.success("Certificate downloaded.");
    } catch {
      toast.error("Failed to download certificate.");
    }
  };

  const handleCertificateSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!certificateFormData || !resultId) {
      toast.error("Certificate data or result ID is missing.");
      return;
    }

    const missingRequiredFields: string[] = [];
    certificateFormFields.forEach((field: CertificateFieldConfig) => {
      if (field.required) {
        const value = certificateFormData[field.key];
        if (value === undefined || value === null || value === "" || String(value).trim() === "") {
          missingRequiredFields.push(field.label);
        }
      }
    });

    if (missingRequiredFields.length > 0) {
      toast.error(`Please fill in required fields: ${missingRequiredFields.join(", ")}`);
      return;
    }

    try {
      setIsGeneratingCertificate(true);
      const cert = await certificateService.generate({
        result_id: resultId,
        date: toDDMMYYYY(certificateFormData.date),
        temperature: certificateFormData.temperature,
        humidity: certificateFormData.humidity,
        atm_pressure: certificateFormData.atmPressure,
        cable_length: certificateFormData.cableLength,
        dwt_no: certificateFormData.dwtNo,
      });
      setCertificateForResult(cert);
      closeCertificateForm();
      const blob = await certificateService.getDownloadBlob(cert.id);
      certificateService.triggerCertificateDownload(blob, cert.filename ?? cert.certificate_number ?? "certificate");
      toast.success("Certificate generated and downloaded.");
    } catch {
      toast.error("Failed to generate certificate. Please try again.");
      setIsGeneratingCertificate(false);
    }
  };

  // Helper to show calibration number based on type and index (same as CalibrationManagement)
  const getCalibrationLabel = (calibration?: any, calibrationIndex?: number, calibrationSequenceNumber?: number) => {
    if (!calibration) return "N/A";

    const typeRaw = calibration.calibration_type || "";
    const type = typeRaw.toLowerCase();
    // Use provided sequence number (oldest=1) when available; fallback to index
    const baseNumber = calibrationSequenceNumber ?? ((calibrationIndex ?? 0) + 1);

    if (type === "initial") {
      return "Calibration 1 (Initial)";
    }

    if (type === "recalibration") {
      // Ensure recalibrations start from 2 even if index is 0
      const number = Math.max(2, baseNumber);
      return `Calibration ${number} (Recalibration)`;
    }

    // Fallback: use the sequence number with raw type
    const labelType = typeRaw ? ` (${typeRaw})` : "";
    return `Calibration ${baseNumber}${labelType}`;
  };

  // Calculate calibration sequence number from sensor's calibrations array
  const getCalibrationSequenceNumber = () => {
    if (!sensor?.calibrations || !calibration) return undefined;

    const calibrations = sensor.calibrations || [];
    const totalCalibrations = calibrations.length;

    // Find the index of current calibration in the array
    const calibrationIndex = calibrations.findIndex((cal: any) => cal.id === calibration.id);

    if (calibrationIndex === -1) return undefined;

    // API gives latest-first, so sequence number is total - index (oldest gets 1)
    return totalCalibrations - calibrationIndex;
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  // If no data in state, redirect back
  useEffect(() => {
    if (!calibration || !sensor) {
      toast.error("Calibration data not found. Redirecting...");
      navigate("/calibrations");
    }
  }, [calibration, sensor, navigate]);

  if (!calibration || !sensor) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Loading calibration details...</p>
        </div>
      </div>
    );
  }

  // Output data merged the same way as the original page: input_data lowest precedence so
  // fields like AOL (raw_frequency[0]) still resolve; summary/output win on overlapping keys.
  const outputData = selectedCalibrationResult?.output_data;
  const mergedOutputData = outputData
    ? {
        ...(calibration?.input_data || selectedCalibrationResult?.input_data || {}),
        ...outputData,
        ...(outputData.summary || {}),
      }
    : null;

  const inputData = calibration.input_data;

  const calibrationLabel = getCalibrationLabel(calibration, undefined, getCalibrationSequenceNumber());

  const showCertificateAction = canShowCertificateAction(
    sensorCategory,
    calibration?.status,
    !!selectedCalibrationResult
  );

  const certificateActionButton = showCertificateAction ? (
    certificateForResult ? (
      <button
        onClick={handleDownloadCertificate}
        className="btn-success flex items-center text-sm"
        title="Certificate already generated. Click to download PDF."
      >
        <FileDown className="h-4 w-4 mr-1" />
        Download certificate
      </button>
    ) : (
      <button
        onClick={openCertificateForm}
        className="btn-generate flex items-center text-sm"
        title="Generate a new certificate. You will fill in details in a form."
      >
        <FileDown className="h-4 w-4 mr-1" />
        Generate certificate
      </button>
    )
  ) : null;

  // "Key results" for Overview: only the fields from the schema's result summary section(s) (no
  // coefficient tables or raw data), rendered as MetricCards so the tab stays scannable at a glance.
  const overviewLabelContext: DisplayLabelContext = { measurementUnit: sensorUnit || "MPa" };
  const overviewSummarySections = (schema.outputData?.sections || []).filter(
    (s) => (s.group || "setup") === "result" && /summary/i.test(s.title)
  );
  const overviewFields: FieldConfig[] = overviewSummarySections.reduce<FieldConfig[]>(
    (acc, section) => acc.concat(section.fields),
    []
  );
  const overviewMetrics = mergedOutputData
    ? overviewFields
        .map((field) => resolveMetricField(field, mergedOutputData, overviewLabelContext))
        .filter((metric): metric is { label: string; value: string; unit?: string } => metric !== null)
    : [];

  const environmentItems = getEnvironmentItems(inputData);

  const hasInputMeasurements = schemaHasGroup(schema.inputData, "measurement");
  const hasOutputMeasurements = schemaHasGroup(schema.outputData, "measurement");
  const hasInputAnalysis = schemaHasGroup(schema.inputData, "analysis");
  const hasOutputAnalysis = schemaHasGroup(schema.outputData, "analysis");

  const tabs: TabItem<TabId>[] = [
    { id: "overview", label: "Overview" },
    { id: "sensor", label: "Sensor Details" },
    { id: "calibration", label: "Calibration" },
    { id: "results", label: "Results" },
    ...(mergeMeasurementsIntoResults ? [] : [{ id: "measurements" as TabId, label: "Measurements" }]),
    { id: "analysis", label: "Analysis" },
  ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center space-x-2 text-sm text-gray-500 border-b border-gray-200 pb-4">
        <button
          onClick={() => navigate("/calibrations")}
          className="hover:text-gray-700 transition-colors cursor-pointer"
          title="Back to Calibrations Management"
        >
          Calibrations Management
        </button>
        <ChevronRight className="h-4 w-4" />
        <span className="text-primary-600 font-medium">Calibration Details</span>
      </div>

      <div className="card">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Calibration Details</h3>
            <p className="text-sm text-gray-600">
              {sensor.serial_number} &middot; {formatSensorType(sensorType)} &middot; {calibrationLabel}
            </p>
          </div>
          {certificateActionButton}
        </div>

        <TabNavigation tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        <div className="p-6">
          {activeTab === "overview" && (
            <div className="space-y-6">
              <StatusCard
                status={calibration.status}
                title="Calibration Result"
                subtitle={`${calibrationLabel} · ${calibration.calibration_type || ""}`}
              />

              <InfoGrid
                items={[
                  { label: "Serial Number", value: sensor.serial_number },
                  { label: "Sensor Type", value: formatSensorType(sensorType) },
                  { label: "Calibration Type", value: calibration.calibration_type },
                  {
                    label: "Calibration Date",
                    value: calibration.created_date
                      ? new Date(calibration.created_date).toLocaleDateString()
                      : null,
                  },
                ]}
              />

              {overviewMetrics.length > 0 && (
                <DetailSection title="Key Results" bgColor="bg-white border border-gray-200">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {overviewMetrics.map((metric, index) => (
                      <MetricCard
                        key={index}
                        label={metric.label}
                        value={metric.value}
                        unit={metric.unit}
                      />
                    ))}
                  </div>
                </DetailSection>
              )}

              {environmentItems.length > 0 && (
                <DetailSection title="Test Environment" bgColor="bg-cyan-50">
                  <InfoGrid items={environmentItems} />
                </DetailSection>
              )}

              {calibration.acceptance_remark && (
                <DetailSection title="Acceptance Remark">
                  <p className="text-sm text-gray-900">{calibration.acceptance_remark}</p>
                </DetailSection>
              )}

              {hasResult && (
                <DetailSection title="Result Overview">
                  <InfoGrid
                    columns={3}
                    items={[
                      { label: "Status", value: getStatusBadge(selectedCalibrationResult.status) },
                      { label: "Created By", value: selectedCalibrationResult.created_by },
                      {
                        label: "Created Date",
                        value: selectedCalibrationResult.created_date
                          ? new Date(selectedCalibrationResult.created_date).toLocaleDateString()
                          : null,
                      },
                    ]}
                  />
                </DetailSection>
              )}
            </div>
          )}

          {activeTab === "sensor" && (
            <div className="space-y-6">
              <DetailSection title="Sensor Information">
                <InfoGrid
                  columns={3}
                  items={[
                    { label: "Serial Number", value: sensor.serial_number },
                    { label: "Category", value: sensorCategory },
                    { label: "Type", value: formatSensorType(sensorType) },
                    { label: "Model", value: sensorModel },
                    {
                      label: "Measurement Range",
                      value:
                        sensorRange != null
                          ? formatMeasurementRangeLabel(sensorRange, sensorUnit, sensorCategory)
                          : null,
                    },
                    { label: "Unit", value: sensorUnit },
                    { label: "Condition", value: sensor.condition },
                  ]}
                />
              </DetailSection>
            </div>
          )}

          {activeTab === "calibration" && (
            <div className="space-y-6">
              <DetailSection title="Calibration Overview">
                <InfoGrid
                  items={[
                    { label: "Calibration", value: calibrationLabel },
                    { label: "Type", value: calibration.calibration_type },
                    { label: "Status", value: getStatusBadge(calibration.status) },
                    { label: "Created By", value: calibration.created_by },
                    {
                      label: "Created Date",
                      value: calibration.created_date
                        ? new Date(calibration.created_date).toLocaleDateString()
                        : null,
                    },
                  ]}
                />
              </DetailSection>

              {inputData ? (
                <DynamicCalibrationDisplay
                  sections={schema.inputData?.sections}
                  tables={schema.inputData?.tables}
                  data={inputData}
                  measurementUnit={sensorUnit}
                  groups={["setup"]}
                />
              ) : (
                <p className="text-sm text-gray-400 italic">Not available</p>
              )}
            </div>
          )}

          {activeTab === "results" && (
            <div className="space-y-6">
              {/*
                Result status/remark live on the Calibration record itself
                (calibration.status / acceptance_remark, computed and saved at
                submission time — see api/routes/calibrations.py
                validate_acceptance_criteria) and are NOT gated on a separate
                CalibrationResult row existing. Showing them here regardless of
                `hasResult` avoids a "no result" message even though the
                calibration's actual pass/fail verdict is already known.
              */}
              <DetailSection title="Result Status">
                <InfoGrid
                  columns={3}
                  items={[
                    { label: "Status", value: getStatusBadge(calibration.status) },
                    { label: "Calibration Type", value: calibration.calibration_type },
                    {
                      label: "Calibration Date",
                      value: calibration.created_date
                        ? new Date(calibration.created_date).toLocaleDateString()
                        : null,
                    },
                  ]}
                />
              </DetailSection>

              {hasResult && mergedOutputData ? (
                <DynamicCalibrationDisplay
                  sections={schema.outputData?.sections}
                  tables={schema.outputData?.tables}
                  data={mergedOutputData}
                  measurementUnit={sensorUnit}
                  groups={["result"]}
                />
              ) : (
                <p className="text-sm text-gray-400 italic">
                  Detailed calculated results (error values, tolerance, coefficients) are not
                  available yet for this calibration.
                </p>
              )}

              {mergeMeasurementsIntoResults && (
                <>
                  {!hasInputMeasurements && !hasOutputMeasurements ? (
                    <p className="text-sm text-gray-400 italic">
                      No raw measurement data available for this sensor type.
                    </p>
                  ) : (
                    <>
                      {hasInputMeasurements && inputData && (
                        <DynamicCalibrationDisplay
                          sections={schema.inputData?.sections}
                          tables={schema.inputData?.tables}
                          data={inputData}
                          measurementUnit={sensorUnit}
                          groups={["measurement"]}
                        />
                      )}
                      {hasOutputMeasurements && mergedOutputData && (
                        <DynamicCalibrationDisplay
                          sections={schema.outputData?.sections}
                          tables={schema.outputData?.tables}
                          data={mergedOutputData}
                          measurementUnit={sensorUnit}
                          groups={["measurement"]}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {!mergeMeasurementsIntoResults && activeTab === "measurements" && (
            <div className="space-y-6">
              {!hasInputMeasurements && !hasOutputMeasurements && (
                <p className="text-sm text-gray-400 italic">
                  No raw measurement data available for this sensor type.
                </p>
              )}
              {hasInputMeasurements && inputData && (
                <DynamicCalibrationDisplay
                  sections={schema.inputData?.sections}
                  tables={schema.inputData?.tables}
                  data={inputData}
                  measurementUnit={sensorUnit}
                  groups={["measurement"]}
                />
              )}
              {hasOutputMeasurements && mergedOutputData && (
                <DynamicCalibrationDisplay
                  sections={schema.outputData?.sections}
                  tables={schema.outputData?.tables}
                  data={mergedOutputData}
                  measurementUnit={sensorUnit}
                  groups={["measurement"]}
                />
              )}
            </div>
          )}

          {activeTab === "analysis" && (
            <div className="space-y-6">
              {!hasInputAnalysis && !hasOutputAnalysis && (
                <p className="text-sm text-gray-400 italic">
                  No engineering analysis data available for this sensor type.
                </p>
              )}
              {hasInputAnalysis && inputData && (
                <DynamicCalibrationDisplay
                  sections={schema.inputData?.sections}
                  tables={schema.inputData?.tables}
                  data={inputData}
                  measurementUnit={sensorUnit}
                  groups={["analysis"]}
                />
              )}
              {hasOutputAnalysis && mergedOutputData && (
                <DynamicCalibrationDisplay
                  sections={schema.outputData?.sections}
                  tables={schema.outputData?.tables}
                  data={mergedOutputData}
                  measurementUnit={sensorUnit}
                  groups={["analysis"]}
                />
              )}
            </div>
          )}
        </div>

        {/* Certificate Form Modal */}
        {showCertificateForm && certificateFormData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between border-b border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Generate certificate
                </h3>
                <button
                  onClick={closeCertificateForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleCertificateSubmit} className="p-6 space-y-6">
                <div className="border border-dashed border-gray-200 rounded-lg p-4 space-y-4">
                  <h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                    {certificateFormTitle}
                  </h4>
                  {certificateFormDescription && (
                    <p className="text-xs text-gray-500">
                      {certificateFormDescription}
                    </p>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {certificateFormFields.map((field: CertificateFieldConfig) => {
                      const value =
                        certificateFormData?.[field.key] ?? "";
                      let inputValue =
                        value === undefined || value === null ? "" : String(value);
                      // Date field is stored as YYYY-MM-DD so use as-is for input type="date"
                      if (field.type === "date" && inputValue) {
                        inputValue = toYYYYMMDD(inputValue) || inputValue;
                      }
                      const inputType =
                        field.type === "number" ? "number" : field.type || "text";

                      const resolvedUnit = resolveCertificateFieldUnit(
                        field.unit,
                        certificateDisplayContext
                      );

                      return (
                        <div
                          key={field.key as string}
                          className={field.gridColumns === 2 ? "md:col-span-2" : ""}
                        >
                          <label className="block text-sm font-medium text-gray-700">
                            {field.label}
                            {field.required && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                            {resolvedUnit && (
                              <span className="text-xs text-gray-500 ml-1">
                                ({resolvedUnit})
                              </span>
                            )}
                          </label>
                          <input
                            type={inputType}
                            className="input mt-1"
                            value={inputValue}
                            placeholder={field.placeholder}
                            required={field.required}
                            onChange={(event) =>
                              handleCertificateFieldChange(
                                field.key,
                                event.target.value
                              )
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeCertificateForm}
                    className="btn-secondary"
                    disabled={isGeneratingCertificate}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isGeneratingCertificate}
                  >
                    {isGeneratingCertificate ? "Generating..." : "Generate and download"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Actions Footer */}
        <div className="px-6 pb-6 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-3 justify-end">
            <button
              onClick={() => navigate("/calibrations")}
              className="btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalibrationDetailsPage;
